import React, { useMemo } from "react";
import { parse, ParseOptions } from "./parser";

export interface MarkdownProps {
  /**
   * The markdown content to be rendered.
   * @example
   * ```md
   * # Hello World
   *
   * This is a **markdown** paragraph with [links](https://example.com)
   * ```
   */
  content: string;

  /**
   * Configuration options for the markdown parser.
   * Includes component overrides, hooks, and global styling.
   * @optional
   */
  options?: ParseOptions;

  /**
   * Additional CSS class names to be applied to the container.
   * @optional
   */
  className?: string;

  /**
   * Custom styles to be applied to the container.
   * @optional
   */
  style?: React.CSSProperties;

  /**
   * Whether to wrap the content in an article element instead of a div.
   * Recommended for semantic HTML when rendering full articles or blog posts.
   * @default false
   * @optional
   */
  asArticle?: boolean;

  /**
   * Custom ID to be applied to the container.
   * @optional
   */
  id?: string;

  /**
   * Additional accessibility attributes for the container.
   * @optional
   */
  aria?: {
    /** Accessible label for the container. */
    label?: string;
    /** ID of the element that describes this container. */
    describedBy?: string;
  };
}

/**
 * A ultra-lightweight React component that renders markdown content with a token-based React renderer.
 * 
 * Key Features:
 * - Token-based rendering (No `dangerouslySetInnerHTML`)
 * - GitHub-style alerts (`> [!NOTE]`)
 * - Footnote support (`[^1]`)
 * - Mathematical blocks (`$$`) and inline math (`$`)
 * - Advanced component overrides via the `components` prop.
 *
 * @example
 * ```tsx
 * import Markdown from "markdown-parser-react";
 * 
 * // Basic usage
 * <Markdown content="# Hello World" />
 *
 * // Advanced usage with component overrides and hooks
 * <Markdown
 *   content={markdownContent}
 *   options={{
 *     customStyles: {
 *       headings: { color: "#10b981" }
 *     },
 *     components: {
 *       h1: ({ children }) => <h1 className="text-4xl font-bold">{children}</h1>,
 *       a: (props) => <CustomLink {...props} />
 *     },
 *     onRenderMath: (content, isBlock) => (
 *       <MyMathRenderer content={content} block={isBlock} />
 *     )
 *   }}
 *   asArticle={true}
 * />
 * ```
 * @remarks
 * This library is open source under the MIT license. Support and customization 
 * are available through [Code Media Labs](https://codemedialabs.in).
 */
export function Markdown({
  content,
  options,
  className,
  style,
  asArticle = false,
  id,
  aria,
}: MarkdownProps) {
  // Memoize parsed content to prevent unnecessary re-renders
  const parsedContent = useMemo(() => {
    try {
      return parse(content, options);
    } catch (error) {
      console.error("Error parsing markdown:", error);
      return [<p key="error">Error parsing markdown content</p>];
    }
  }, [content, options]);

  // Common props for container
  const containerProps = {
    className: className
      ? `markdown-container ${className}`
      : "markdown-container",
    style,
    id,
    ...(aria?.label && { "aria-label": aria.label }),
    ...(aria?.describedBy && { "aria-describedby": aria.describedBy }),
  };

  // Use article tag for full content, div for partial content
  const Container = asArticle ? "article" : "div";

  return <Container {...containerProps}>{parsedContent}</Container>;
}

export const defaultStyles = `
.markdown-container {
  font-family: system-ui, -apple-system, "Segoe UI", Roboto, Helvetica, Arial, sans-serif;
  line-height: 1.6;
  color: #1f2328;
  max-width: 100%;
}
.markdown-container h1, .markdown-container h2 {
  border-bottom: 1px solid #d0d7de;
  padding-bottom: 0.3em;
  margin-top: 1.5em;
}
.markdown-container a {
  color: #0969da;
  text-decoration: none;
}
.markdown-container a:hover {
  text-decoration: underline;
}
.markdown-container img {
  max-width: 100%;
  height: auto;
  border-radius: 6px;
}
.markdown-container pre {
  overflow-x: auto;
  padding: 16px;
  background-color: #f6f8fa;
  border-radius: 6px;
  font-size: 85%;
  line-height: 1.45;
}
.markdown-container code {
  padding: 0.2em 0.4em;
  margin: 0;
  font-size: 85%;
  background-color: rgba(175, 184, 193, 0.2);
  border-radius: 6px;
  font-family: ui-monospace, SFMono-Regular, SF Mono, Menlo, Consolas, Liberation Mono, monospace;
}
.markdown-container pre code {
  background-color: transparent;
  padding: 0;
}
.markdown-container blockquote {
  border-left: 0.25em solid #d0d7de;
  margin: 0;
  padding: 0 1em;
  color: #636c76;
}
.markdown-container table {
  border-spacing: 0;
  border-collapse: collapse;
  margin-top: 0;
  margin-bottom: 16px;
  width: 100%;
}
.markdown-container table th, .markdown-container table td {
  padding: 6px 13px;
  border: 1px solid #d0d7de;
}
.markdown-container table tr {
  background-color: #ffffff;
  border-top: 1px solid #d0d7de;
}
.markdown-container table tr:nth-child(2n) {
  background-color: #f6f8fa;
}
.markdown-container .task-list-item {
  list-style-type: none;
}
.markdown-container .task-list-item input[type="checkbox"] {
  cursor: pointer;
  width: 1.2rem;
  height: 1.2rem;
  margin: 0.2rem 0.5rem 0 0;
  accent-color: #0969da;
  flex-shrink: 0;
}
.markdown-container .markdown-alert {
  padding: 1rem 1rem 1rem 1.25rem;
  margin-bottom: 16px;
  color: inherit;
  border-left: 0.25em solid #d0d7de;
  border-radius: 6px;
}
.markdown-alert > *:last-child {
  margin-bottom: 0;
}
.markdown-alert > *:first-child {
  margin-top: 0;
}
.markdown-alert-note { border-left-color: #0969da !important; background-color: #f0f7ff; }
.markdown-alert-tip { border-left-color: #1a7f37 !important; background-color: #f0fff4; }
.markdown-alert-important { border-left-color: #8250df !important; background-color: #f8f5ff; }
.markdown-alert-warning { border-left-color: #9a6700 !important; background-color: #fff8ec; }
.markdown-alert-caution { border-left-color: #cf222e !important; background-color: #fff1f0; }

.markdown-container .math-block {
  overflow-x: auto;
  padding: 1rem 0;
  font-size: 110%;
}
.markdown-container .task-list-item input[type="checkbox"] {
  cursor: pointer;
  width: 1.1em;
  height: 1.1em;
  accent-color: #0969da;
}
.markdown-container .footnotes {
  font-size: 0.85em;
  color: #636c76;
  margin-top: 2rem;
}
`;

export default Markdown;
